import { useState } from 'react';
import { Alert, Button, Form, Input, Space, Table, Tag, Typography, message } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { RetweetOutlined, SearchOutlined } from '@ant-design/icons';
import { Authentication } from '@leedpulse-monorepo/classes';
import migrateSubscriptionSender, {
  SenderMigrationOtherSender,
  SenderMigrationResponse,
} from '../../../domain/use_cases/subscriptions/migrateSubscriptionSender';

interface MigrateSenderModalProps {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  oldSender: string;
  /** Redirection currently set on the number being replaced — prefilled and editable. */
  currentRedirection: string;
  auth: Authentication | null;
  onSuccess: () => void;
}

const WARNING_TITLE: Record<string, string> = {
  foreign_api_key_left_behind: 'Documents laissés en place',
  old_sender_still_held: 'Ancien numéro encore utilisé',
};

const REDIRECTION_SOURCE_LABEL: Record<string, string> = {
  param: 'saisie manuelle',
  old_number: "héritée de l'ancien numéro",
  new_number: 'déjà posée sur le nouveau numéro',
  none: 'aucune',
};

const MigrateSenderModal: React.FC<MigrateSenderModalProps> = ({
  open,
  onClose,
  subscriptionId,
  oldSender,
  currentRedirection,
  auth,
  onSuccess,
}) => {
  const [newSender, setNewSender] = useState('');
  const [redirection, setRedirection] = useState(currentRedirection);
  const [response, setResponse] = useState<SenderMigrationResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const plan = response?.plan ?? null;
  const isApplied = response?.applied === true;
  // Lancé sans être attendu : la réécriture tourne côté serveur, il n'y a pas de
  // résultat à afficher. L'écran se verrouille comme s'il était terminé.
  const isDetached = response?.detached === true;
  const isDone = isApplied || isDetached;
  const hasBlockers = (plan?.blockers.length ?? 0) > 0;

  const resetPlan = () => setResponse(null);

  const call = async (apply: boolean) => {
    if (!auth) return;
    const cleaned = newSender.replace(/\D/g, '');
    if (!cleaned) {
      message.error('Renseigne le nouveau numéro.');
      return;
    }
    if (cleaned === oldSender.replace(/\D/g, '')) {
      message.error("Le nouveau numéro est identique à l'ancien.");
      return;
    }

    const cleanedRedirection = redirection.replace(/\D/g, '');
    const setLoading = apply ? setIsBusy : setIsAnalyzing;
    setLoading(true);
    try {
      const result = await migrateSubscriptionSender(
        {
          subscription_id: subscriptionId,
          old_sender: oldSender,
          new_sender: cleaned,
          // Inchangé = héritage de l'ancien numéro, pas une saisie : le laisser
          // remonter ferait afficher « saisie manuelle » sur une valeur préremplie.
          redirection:
            cleanedRedirection && cleanedRedirection !== currentRedirection.replace(/\D/g, '')
              ? cleanedRedirection
              : undefined,
          apply,
        },
        auth
      );
      setResponse(result);
      if (result.applied) {
        message.success(`${result.result?.migrated_documents ?? 0} document(s) migré(s) sur ${cleaned}.`);
        onSuccess();
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Erreur lors de l'analyse de la migration.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewSender('');
    setRedirection(currentRedirection);
    setResponse(null);
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RetweetOutlined size={20} style={{ marginRight: 6 }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Remplacer le numéro {oldSender}</span>
        </div>
      }
      centered
      open={open}
      onCancel={handleClose}
      closable={!isBusy}
      maskClosable={!isBusy}
      okText={
        isApplied
          ? 'Migration terminée'
          : isDetached
            ? 'Migration lancée'
            : plan
              ? plan.rewrites_history ? `Migrer ${plan.total_documents} document(s)` : 'Remplacer le numéro'
              : 'Migrer'
      }
      cancelText={isDone ? 'Fermer' : 'Annuler'}
      confirmLoading={isBusy}
      okButtonProps={{ danger: true, disabled: !plan || hasBlockers || isDone }}
      onOk={() => call(true)}
      width={760}
    >
      <Form layout="vertical" style={{ marginTop: '1.5rem' }}>
        <Form.Item label="Nouveau numéro">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={newSender}
              placeholder="33XXXXXXXXX"
              disabled={isDone}
              onChange={(e) => {
                setNewSender(e.target.value);
                resetPlan();
              }}
              onPressEnter={() => call(false)}
            />
            <Button icon={<SearchOutlined />} loading={isAnalyzing} disabled={isDone} onClick={() => call(false)}>
              Analyser
            </Button>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Numéro de redirection"
          extra={
            currentRedirection
              ? `Reprise de la redirection de ${oldSender}, modifiable.`
              : `${oldSender} n'a aucune redirection. Tant que ce champ est vide, tout appel entrant est rejeté.`
          }
        >
          <Input
            value={redirection}
            placeholder="33XXXXXXXXX"
            disabled={isDone}
            onChange={(e) => {
              setRedirection(e.target.value);
              resetPlan();
            }}
          />
        </Form.Item>

        {isDetached && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Migration de ${plan?.total_documents ?? 0} document(s) lancée en arrière-plan`}
            description={
              <>
                C'est trop volumineux pour attendre la réponse à l'écran. La réécriture continue
                côté serveur, tu peux fermer cette fenêtre. Reviens dans quelques minutes et
                relance l'analyse : quand elle annonce 0 document à réécrire, tout est passé.
              </>
            }
          />
        )}

        {plan?.exceeds_sync_limit && !isDone && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Migration trop volumineuse pour être attendue à l'écran"
            description={`${plan.total_documents} documents : la migration sera lancée en arrière-plan et tu devras revenir relancer l'analyse pour vérifier qu'il ne reste rien.`}
          />
        )}

        {plan?.blockers.map((blocker) => (
          <Alert
            key={blocker.code + blocker.message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Migration bloquée"
            description={
              <>
                <div>{blocker.message}</div>
                {blocker.details.map((detail) => (
                  <div key={detail} style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                    {detail}
                  </div>
                ))}
              </>
            }
          />
        ))}

        {plan?.warnings.map((warning) => (
          <Alert
            key={warning.code + warning.message}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={WARNING_TITLE[warning.code] ?? 'Avertissement'}
            description={
              <>
                <div>{warning.message}</div>
                {warning.details.map((detail) => (
                  <div key={detail} style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4, whiteSpace: 'pre' }}>
                    {detail}
                  </div>
                ))}
              </>
            }
          />
        ))}

        {plan && !hasBlockers && (
          <>
            <Alert
              type={isApplied ? 'success' : 'info'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                isApplied
                  ? `${response?.result?.migrated_documents ?? 0} document(s) migré(s) — Logs/${response?.result?.log_id}`
                  : plan.rewrites_history
                    ? `${plan.total_documents} document(s) à réécrire`
                    : 'Leedflow : les contacts et leur historique restent sur l\'abonnement, aucun document à réécrire'
              }
              description={
                <>
                  Redirection posée sur {plan.new_sender} :{' '}
                  <Tag color={plan.redirection ? 'green' : 'red'}>{plan.redirection || 'aucune'}</Tag>
                  <Typography.Text type="secondary">
                    ({REDIRECTION_SOURCE_LABEL[plan.redirection_source]})
                  </Typography.Text>
                </>
              }
            />

            {/* Information seule : déplacer ces documents est une opération de
                maintenance à part, sur la page contacts. Ici on se contente de
                signaler leur existence, c'est le moment où quelqu'un a le contexte. */}
            {plan.other_senders.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Cet abonnement a des contacts sur d'autres numéros"
                description={
                  <>
                    {plan.other_senders.map((other: SenderMigrationOtherSender) => (
                      <div key={other.sender} style={{ marginBottom: 4 }}>
                        <Tag color={other.still_held ? 'blue' : 'orange'}>{other.sender}</Tag>
                        {other.contacts} contact(s){' '}
                        <Typography.Text type="secondary">
                          {other.still_held ? '— deuxième ligne active' : '— égaré, plus rattaché'}
                        </Typography.Text>
                      </div>
                    ))}
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      Ce swap n'y touche pas. Pour les regrouper sur un seul numéro : page Contacts
                      de l'abonnement, menu « … », « Réaffecter un numéro ».
                    </Typography.Text>
                  </>
                }
              />
            )}

            {plan.rewrites_history && (
              <Table
                size="small"
                pagination={false}
                style={{ marginBottom: 16 }}
                dataSource={plan.counts.map((c) => ({ key: c.collection, ...c }))}
                columns={[
                  { title: 'Collection', dataIndex: 'collection', key: 'collection' },
                  { title: 'Documents', dataIndex: 'total', key: 'total' },
                  {
                    title: 'Sans api_key',
                    dataIndex: 'orphans',
                    key: 'orphans',
                    render: (value: number) => (value ? <Tag color="orange">{value}</Tag> : '—'),
                  },
                  {
                    title: 'Laissés en place',
                    dataIndex: 'foreign',
                    key: 'foreign',
                    render: (value: number) => (value ? <Tag color="gold">{value}</Tag> : '—'),
                  },
                  ...(isApplied
                    ? [
                        {
                          title: 'Restant sur ' + plan.old_sender,
                          key: 'remaining',
                          render: (_: unknown, row: { collection: string; foreign: number }) => {
                            const remaining = response?.result?.remaining_on_old.find(
                              (r) => r.collection === row.collection
                            );
                            const total = remaining?.total ?? 0;
                            // Green when only the previous tenant's documents are left:
                            // they were deliberately not migrated.
                            return <Tag color={total > row.foreign ? 'red' : 'green'}>{total}</Tag>;
                          },
                        },
                      ]
                    : []),
                ]}
              />
            )}

            <Table
              size="small"
              pagination={false}
              title={() => 'Champs réécrits'}
              locale={{ emptyText: 'Rien à réécrire — déjà à jour' }}
              dataSource={plan.changes.map((c) => ({ key: c.target, ...c }))}
              columns={[
                { title: 'Champ', dataIndex: 'target', key: 'target' },
                { title: 'Avant', dataIndex: 'from', key: 'from', ellipsis: true },
                { title: 'Après', dataIndex: 'to', key: 'to', ellipsis: true },
              ]}
            />
          </>
        )}

        {(!plan || plan.rewrites_history) && (
          <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: 16 }}>
            Tous les contacts rattachés à {oldSender}, ainsi que leurs SMS, appels et messageries vocales,
            seront migrés vers le nouveau numéro.
          </Typography.Text>
        )}
      </Form>
    </Modal>
  );
};

export default MigrateSenderModal;
